// Domain constants for the invoice-processing workflow.
// Sources of truth: schema.md (status/step/decision enums), security.md (roles/teams),
// app.md §6/§9/§12, and ai/current - two flows/ai.md (flag types + resolution policy).

// ---- Roles -----------------------------------------------------------------
// UX-routing roles only. Real access is enforced by Dataverse RLS + the broker flow.
// Three-tier linear approval (planning §3): Department → Director → Oversight
// replace the old Primary/Secondary pair. DirectorApproval keeps the
// owner-team semantics the old PrimaryApproval had (the company team that owns
// the invoice); DepartmentApproval is a new per-company tier that owns nothing;
// OversightApproval is the renamed internal Secondary.
export type Role =
  | 'ImportControl'
  | 'DepartmentApproval'
  | 'DirectorApproval'
  | 'OversightApproval'
  | 'ExportControl'
  | 'ManualEntry'
  | 'ClosingConfirmation'
  | 'DenialReview'
  | 'Archives'
  | 'Admin'
  | 'Training'
  // Per-company fleet maintenance (2026-08-03). NOT a pipeline step: it grants no
  // queue and lights no timeline node. It exists so a company's transportation
  // staff can maintain vehicles without any invoice or claim access — their
  // Dataverse role carries vehicles C/R/W and nothing else
  // (security.md -> vehicles). companyName carries which company's fleet.
  | 'FleetManagement'

// ---- Workspaces -------------------------------------------------------------
// A workspace is a class of document flowing through the pipeline. Invoices is
// the only one at launch; the level is kept (state, label map, per-workspace
// role projection) so adding a second one is a data change rather than a
// refactor. Admin / References / Analytics are workspace-independent.
export type Workspace = 'invoice'
export const WORKSPACES: readonly Workspace[] = ['invoice']
export const WORKSPACE_LABEL: Record<Workspace, string> = { invoice: 'Invoices' }

// ---- Invoice status enum (invoices.status) ------------------
export const InvoiceStatus = {
  PendingMatching: 'Pending Matching',
  MatchingFailed: 'Matching Failed',
  PendingImportControl: 'Pending Import Control Decision',
  // Three-tier linear approval (planning §3): Department → Director → Oversight.
  PendingDepartment: 'Pending Department Approval Decision',
  PendingDirector: 'Pending Director Approval Decision',
  PendingOversight: 'Pending Oversight Approval Decision',
  PendingExportControl: 'Pending Export Control Decision',
  PendingManualEntry: 'Pending Manual Entry Decision',
  PendingDenialReview: 'Pending Denial Review Decision',
  PendingExport: 'Pending Export',
  // Terminal happy-path statuses, written by the broker at the confirmation
  // step — descriptive per pathway (replaced the single 'Exported' 2026-07-12):
  BulkImported: 'Bulk Imported Into Point Click Care', // Closing Confirmation (CSV batch)
  ManuallyEntered: 'Manually Entered Into Point Click Care', // Manual Entry (Process C)
  ArchivedWithoutPayment: 'Archived Without Payment',
  ArchivedForFreshInvoice: 'Archived For Fresh Invoice',
  ArchivedNotAnInvoice: 'Archived Not An Invoice',
} as const
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus]

// ---- Decision step + outcome enums (decisions) -------------------------
export const DecisionStep = {
  ImportControl: 'Import Control Decision',
  Department: 'Department Approval Decision',
  Director: 'Director Approval Decision',
  Oversight: 'Oversight Approval Decision',
  ExportControl: 'Export Control Decision',
  ManualEntry: 'Manual Entry Decision',
  ClosingConfirmation: 'Closing Confirmation Decision',
  DenialReview: 'Denial Review Decision',
} as const
export type DecisionStep = (typeof DecisionStep)[keyof typeof DecisionStep]

export const DecisionOutcome = {
  Approved: 'approved',
  Denied: 'denied',
  ArchivedWithoutPayment: 'archived without payment',
  ArchivedForFreshInvoice: 'archived for fresh invoice',
  ArchivedNotAnInvoice: 'archived not an invoice',
} as const
export type DecisionOutcome = (typeof DecisionOutcome)[keyof typeof DecisionOutcome]

// Outcomes that require a reason comment before they commit.
export const REASON_REQUIRED: ReadonlySet<string> = new Set([
  DecisionOutcome.Denied,
  DecisionOutcome.ArchivedWithoutPayment,
  DecisionOutcome.ArchivedForFreshInvoice,
  DecisionOutcome.ArchivedNotAnInvoice,
])

// ---- Assignments-based authorization ---------------------------------------
// The three approval tiers (Department / Director / Oversight) authorize from an
// active appointment for the record's company — the appointment row IS the
// permission. These are the steps where the GL account is editable but nothing
// else is; the API decides who may, by step, and writes the correction row
// itself (api-contract §4.6).
export const APPROVAL_TIER_STEPS: ReadonlySet<string> = new Set([
  DecisionStep.Department,
  DecisionStep.Director,
  DecisionStep.Oversight,
])

// assignments.role — who may act at a tier. All three approval tiers are
// company-scoped (company_id/name, 'Other' = catch-all), 'oversight' included
// since 2026-08-10 (issue 2).
export const ASSIGNMENT_ROLES = {
  oversight: 'oversight',
  director: 'director',
  departmentHead: 'department-head',
  // Fleet maintenance (2026-08-03). An appointment, not a team membership, so it
  // sits in its company's group on the Assignments screen beside the director and
  // department heads (user ruling). It is NOT an approval tier: it confers no
  // decision authority and no record access — it unlocks the vehicles master and
  // nothing else (security.md -> vehicles).
  fleet: 'fleet',
} as const
export type AssignmentRole = (typeof ASSIGNMENT_ROLES)[keyof typeof ASSIGNMENT_ROLES]

export const ASSIGNMENT_ROLE_LABEL: Record<string, string> = {
  oversight: 'Oversight',
  director: 'Director',
  'department-head': 'Department head',
  fleet: 'Fleet management',
}

/** The three approval tiers. `fleet` is deliberately absent — it is an
    appointment but not an authority to decide anything. */
export const APPROVAL_ASSIGNMENT_ROLES: readonly string[] = [
  ASSIGNMENT_ROLES.oversight,
  ASSIGNMENT_ROLES.director,
  ASSIGNMENT_ROLES.departmentHead,
]

export function assignmentRoleLabel(role: string | undefined | null): string {
  if (!role) return ''
  return ASSIGNMENT_ROLE_LABEL[role] ?? role
}

// assignments.action + request_status.
export const ASSIGNMENT_ACTION = { appoint: 'appoint', retire: 'retire' } as const
export const ASSIGNMENT_STATUS = { pending: 'Pending', applied: 'Applied', rejected: 'Rejected' } as const

// ---- Deny / archive reason categories (decisions.reason_category) ----
// A coarse tag the reviewer may pick alongside the free-text reason on a deny
// or archive. Optional — the free-text reason stays the record of why. The
// stored value is the stable left-hand key; the label is the dropdown display.
export const REASON_CATEGORIES = {
  price_discrepancy: 'Price discrepancy',
  wrong_company: 'Wrong company',
  duplicate: 'Duplicate',
  unauthorized_purchase: 'Unauthorized purchase',
  other: 'Other',
} as const
export type ReasonCategory = keyof typeof REASON_CATEGORIES

export function reasonCategoryLabel(key: string | undefined | null): string {
  if (!key) return ''
  return (REASON_CATEGORIES as Record<string, string>)[key] ?? key
}

// ---- Upload + export status enums ------------------------------------------
export const UploadStatus = {
  Received: 'Received',
  Parsed: 'Parsed',
  Failed: 'Failed',
  // Mixed-upload gate (incorporating-claims.md; schema.md): the Extracting flow
  // refused the file (a claim beside another document type, or an app upload
  // expecting a claim with no recognizable CMS-1500). No rows written; the
  // uploader gets a rejection email.
  Rejected: 'Rejected',
} as const

export const ExportStatus = {
  Received: 'Received',
  Completed: 'Completed',
  Failed: 'Failed',
} as const

// ---- Flag types + resolution policy ----------------------------------------
// Auto-resolution flags clear on save when their underlying condition is satisfied.
// Explicit-resolution flags clear only via a per-flag "Mark resolved" click.
export type FlagResolution = 'auto' | 'explicit'

export const FLAG_TYPES: Record<string, { label: string; resolution: FlagResolution; description: string }> = {
  MissingTotal: { label: 'Missing Total', resolution: 'auto', description: 'No invoice total was found. Please enter one.' },
  MissingExternalInvoiceId: { label: 'Missing External Invoice ID', resolution: 'auto', description: 'No vendor-issued invoice number was found. Please enter one.' },
  // Redefined 2026-07-12 (v1.34) to match the step-5 release gate exactly:
  // line items = TOTAL AND subtotal + tax = total. The line items carry the sales
  // tax (2026-07-29, REPORT.md Q1), so they are compared against the total, not
  // the pre-tax subtotal — a pre-tax-lines invoice is what exported short.
  LineItemsToTotalMismatch: { label: 'Amounts Don’t Reconcile', resolution: 'auto', description: 'The line items must add up to the total, with each line item including its share of the sales tax, and subtotal plus tax must equal the total. Please correct the amounts, so they reconcile.' },
  MissingVendor: { label: 'Missing Vendor', resolution: 'auto', description: 'A vendor from our system could not be matched to the vendor information from the invoice. Please search and select one.' },
  DuplicateVendor: { label: 'Duplicate Vendor', resolution: 'explicit', description: 'Several vendors share this name; the first was selected automatically. Please set the correct vendor and then mark this as resolved.' },
  MissingCompany: { label: 'Missing Company', resolution: 'auto', description: 'A company from our system could not be matched to the company information from the invoice. Please search and select one.' },
  MissingExpenseType: { label: 'Missing Expense Type', resolution: 'auto', description: 'One or more line items could not be assigned an expense type. Please search and select what each applicable line item is.' },
  InvoiceExtractionLowConfidence: { label: 'Low Invoice Confidence', resolution: 'explicit', description: 'The extraction agent was unsure about one or more invoice-level fields. Please verify what was returned and then mark this as resolved.' },
  LineItemExtractionLowConfidence: { label: 'Low Line Item Confidence', resolution: 'explicit', description: 'The extraction agent was unsure about one or more line-item-level fields. Please verify what was returned and then mark this as resolved.' },
  VendorMatchLowConfidence: { label: 'Low Vendor Confidence', resolution: 'explicit', description: 'The extraction agent was unsure about the selected vendor. Please verify what was returned and then mark this as resolved.' },
  CompanyMatchLowConfidence: { label: 'Low Company Confidence', resolution: 'explicit', description: 'The extraction agent was unsure about the selected company. Please verify what was returned and then mark this as resolved.' },
  ExpenseTypeMatchLowConfidence: { label: 'Low Expense Type Confidence', resolution: 'explicit', description: 'The extraction agent was unsure about a selected expense type. Please verify what was returned and then mark this as resolved.' },
  NonUSDCurrency: { label: 'Non-USD Currency', resolution: 'explicit', description: 'This invoice is not priced in US Dollars. Please verify the currency and the amounts and then mark this as resolved.' },
  DuplicateDeterministic: { label: 'Exact Duplicate Detected', resolution: 'explicit', description: 'This may be a duplicate of a prior invoice. Please verify and then mark this as resolved (or deny this invoice, if true).' },
  DuplicateFuzzy: { label: '“Fuzzy” Duplicate Detected', resolution: 'explicit', description: 'This may be a duplicate of a prior invoice. Please verify and then mark this as resolved (or deny this invoice, if true).' },
  CreditMemo: { label: 'Possible Credit', resolution: 'explicit', description: 'This invoice may actually contain a credit — the amounts are negative. Please verify amounts and then mark this as resolved.' },
  UnrecognizedGLCode: { label: 'Unrecognized GL Code', resolution: 'explicit', description: 'A GL code was included in or on the document, but it does not seem to match the existing chart of accounts. Please verify the account and then mark this as resolved.' },
  GLCodeLineMismatch: { label: 'GL Code Maybe Misapplied', resolution: 'explicit', description: 'The document contained one GL code, which was applied to every line. However, the line descriptions point to a different expense type. Please verify the coding per line item and then mark this as resolved.' },
  NonInvoice: { label: 'Not A Standard Invoice', resolution: 'explicit', description: 'This document does not look like an invoice. Please verify that it should be paid and then mark this as resolved — or archive it as “not an invoice.”' },
  CompanyInferredFromLetterhead: { label: 'Company Inferred From Letterhead', resolution: 'explicit', description: 'The billed company was inferred from the letterhead, rather than a proper “bill to.” Please verify the company and then mark this as resolved.' },
  InternalDocument: { label: 'Internal Document', resolution: 'explicit', description: 'The “vendor” on this document is one of our own companies. Please verify that this is an invoice to be paid and then mark this as resolved.' },
  // Raised (invoice + claim) when the Extracting flow hit the 40-line-item cap on
  // an itemized bill (phone/utility/fuel). The count itself is not a defect —
  // this flag reports the consequence of capping (lines may be missing, or
  // extraneous content captured) and suppresses LineItemsToTotalMismatch, whose
  // "correct the amounts" wording would misdirect the reviewer (planning §5).
  MaximumLineItemsExtracted: { label: 'Maximum Number Of Line Items Extracted', resolution: 'explicit', description: 'Maximum Number Of Line Items Extracted. There may be more lines to this invoice, or extraneous information may have been extracted. Please verify and then mark this as resolved.' },
}

export function flagLabel(flag: string | undefined): string {
  if (!flag) return 'Flag'
  return FLAG_TYPES[flag]?.label ?? flag
}

export function flagResolution(flag: string | undefined): FlagResolution {
  if (!flag) return 'explicit'
  return FLAG_TYPES[flag]?.resolution ?? 'explicit'
}

export function flagDescription(flag: string | undefined): string {
  if (!flag) return ''
  return FLAG_TYPES[flag]?.description ?? ''
}

// ---- Company routing -------------------------------------------------------
/** PACE Consulting Solutions (`companies.md` id 153) — the company a
    mixed-company invoice is assigned to. When an invoice's line items point
    at more than one company, no single operating company can approve it, so the
    invoice is billed to PACE Consulting Solutions (which has actual staff to
    hand the approvals to) and keyed into Point Click Care by hand (user rule
    2026-07-31; resolveInvoiceCompany in `lib/export.ts`). */
export const PCS_COMPANY_ID = '153'
export const PCS_COMPANY_NAME = 'PACE Consulting Solutions'

// ---- Company source (invoices.company_source) -----------------------
// Provenance of company_id (schema.md § invoices). 'UploaderDomain' and
// 'AiMatched' are written by the Matching flow; the app writes 'Manual' when the
// reviewer picks a company and 'LineCompanies' when the line items set it —
// a derived value, not a human pick, so it is labelled as its own source.
export const COMPANY_SOURCE = {
  UploaderDomain: 'UploaderDomain',
  AiMatched: 'AiMatched',
  Manual: 'Manual',
  LineCompanies: 'LineCompanies',
} as const

// ---- Expense-type source (invoice_line_items.expense_type_source) -----------
// Provenance of a line item's EXPENSE TYPE — "what is it?", Phase A of the
// matching ladder: vendor coding rule → precedent → the document's printed code
// → pass 2 (docs/workflow.md; docs/schema.md) — or 'Manual' when a reviewer sets
// it. Still named `account_source` in the database, because that enum is what
// Phase A resolves.
export const ACCOUNT_SOURCE_LABELS: Record<string, string> = {
  Precedent: 'Reused from a prior invoice',
  DocumentPrinted: 'From document (printed)',
  VendorRule: 'Vendor coding rule',
  AiInferred: 'AI suggestion',
  Manual: 'Set manually',
}

export function accountSourceLabel(source: string | undefined | null): string {
  if (!source) return ''
  return ACCOUNT_SOURCE_LABELS[source] ?? source
}

// ---- Cost-center source (invoice_line_items.cost_center_source) -------------
// Provenance of a line item's COST CENTER — "who is it for?", Phase B. A
// DELIBERATELY DIFFERENT DOMAIN from the expense type's: there is no AI
// suggestion, because a line description cannot reveal the department. The
// values it does share with the expense type mean the same thing, which is
// exactly why a correction distribution must group by (field, old_source).
export const COST_CENTER_SOURCE_LABELS: Record<string, string> = {
  ChartImplied: 'Implied by the chart',
  VendorRule: 'Vendor coding rule',
  DocumentPrinted: 'From document (printed)',
  Manual: 'Set manually',
}

export function costCenterSourceLabel(source: string | undefined | null): string {
  if (!source) return ''
  return COST_CENTER_SOURCE_LABELS[source] ?? source
}

// ---- Toaster ----------------------------------------------------------------
// One app-wide Toaster (mounted in App.tsx); screens dispatch to it by id.
export const TOASTER_ID = 'invoice-processing-toaster'

// ---- Timeline nodes (the single-page step timeline) ------------------------
// Main-path order: Import Control → Department → Director → Oversight →
// Export Control → Closing Confirmation, with Denial Review as a branch node.
export type NodeKey =
  | 'import-control'
  | 'department'
  | 'director'
  | 'oversight'
  | 'export-control'
  | 'manual-entry'
  | 'closing-confirmation'
  | 'denial-review'
  | 'archives'

export interface TimelineNode {
  key: NodeKey
  label: string
  role: Role
  /** Statuses whose invoices appear in this node's queue(s). */
  queueStatuses: InvoiceStatus[]
  /** Branch node (Denial Review) renders off the main chain. */
  branch?: boolean
  /** Fork node (Manual Entry): the chain splits after this main-path node —
      "--<" — with the rest of the main path as the top branch and this node
      as the bottom branch (a parallel path, not a sequential step). */
  forkFrom?: NodeKey
  /** Archive node: a terminal-record lookup, not a work step. Renders to the
      right of the branch nodes past its own vertical divider, shows an archive
      glyph instead of a waiting-count badge, and is excluded from the badge
      polling (its queue is unbounded — the screen paginates instead). */
  archive?: boolean
}

export const TIMELINE_NODES: TimelineNode[] = [
  {
    key: 'import-control',
    label: 'Import Control',
    role: 'ImportControl',
    queueStatuses: [InvoiceStatus.PendingImportControl],
  },
  {
    // Department Approval (planning §3) — the company department head, present
    // if its team is staffed; the broker skips it when unstaffed.
    key: 'department',
    label: 'Department Approval',
    role: 'DepartmentApproval',
    queueStatuses: [InvoiceStatus.PendingDepartment],
  },
  {
    key: 'director',
    label: 'Director Approval',
    role: 'DirectorApproval',
    queueStatuses: [InvoiceStatus.PendingDirector],
  },
  {
    key: 'oversight',
    label: 'Oversight Approval',
    role: 'OversightApproval',
    queueStatuses: [InvoiceStatus.PendingOversight],
  },
  {
    key: 'export-control',
    label: 'Export Control',
    role: 'ExportControl',
    queueStatuses: [InvoiceStatus.PendingExportControl],
  },
  {
    // The step-11 checkpoint, split out of Export Control 2026-07-12 (v1.22).
    // Its own team/role (v1.23) — the node stays dark until the user creates
    // "Invoice Processing - Closing Confirmation" and adds members.
    key: 'closing-confirmation',
    label: 'Closing Confirmation',
    role: 'ClosingConfirmation',
    queueStatuses: [InvoiceStatus.PendingExport],
  },
  {
    // Manual-entry pathway (v1.30): invoices marked at step 5 skip the CSV
    // path after the last approval step and land here instead. The pipeline
    // renders the split after Oversight Approval — Export Control above,
    // Manual Entry below ("--<", v1.31).
    key: 'manual-entry',
    label: 'Manual Entry',
    role: 'ManualEntry',
    queueStatuses: [InvoiceStatus.PendingManualEntry],
    forkFrom: 'oversight',
  },
  {
    key: 'denial-review',
    label: 'Denial Review',
    role: 'DenialReview',
    queueStatuses: [InvoiceStatus.PendingDenialReview],
    branch: true,
  },
  {
    // Archives (v1.39): read-only lookup of every terminal invoice — the two
    // Point Click Care completions and the three archived states. Not a work
    // step; it sits to the right of Denial Review past a second vertical
    // divider. No count badge (the set is unbounded — the screen paginates), so
    // queueStatuses is documentary here rather than used for a badge query.
    key: 'archives',
    label: 'Archives',
    role: 'Archives',
    queueStatuses: [
      InvoiceStatus.BulkImported,
      InvoiceStatus.ManuallyEntered,
      InvoiceStatus.ArchivedWithoutPayment,
      InvoiceStatus.ArchivedForFreshInvoice,
      InvoiceStatus.ArchivedNotAnInvoice,
    ],
    archive: true,
  },
]

export function nodeForRole(role: Role): TimelineNode | undefined {
  return TIMELINE_NODES.find((n) => n.role === role)
}

// ---- Internal step roles ----------------------------------------------------
// The API returns roles, not team names: the old client parsed
// "Invoice Processing - Director Approval - <company>" strings to work out what
// a user could do, and that parsing was the fragile part. These are the SEVEN
// `user_roles` tokens the profile payload carries (api-contract §0.3), mapped to
// the UI role names.
//
// `OversightApproval` is deliberately absent: Oversight is not a `user_roles`
// row at all. An active `oversight` appointment IS the whole grant — the tier's
// decision authority and its document access — so the role arrives via
// `assignments`, not here (see identity.ts). It is NOT cross-company: that was
// true only while an oversight row carried a NULL company, which ended
// 2026-08-10; the seat now reaches its own company plus, on the `Other` anchor,
// that anchor's catch-all members, exactly like the other two tiers.
//
// The stored token for the References screen is `references`; `Training` is only
// the internal UI name the screen kept.
export const USER_ROLE_TOKENS = {
  ImportControl: 'import_control',
  ExportControl: 'export_control',
  ClosingConfirmation: 'closing_confirmation',
  ManualEntry: 'manual_entry',
  DenialReview: 'denial_review',
  Admin: 'admin',
  Training: 'references',
} as const satisfies Partial<Record<Role, string>>

export type UserRoleToken = (typeof USER_ROLE_TOKENS)[keyof typeof USER_ROLE_TOKENS]

const ROLE_BY_TOKEN: Record<string, Role> = Object.fromEntries(
  Object.entries(USER_ROLE_TOKENS).map(([role, token]) => [token, role as Role]),
)

/** The UI role for an internal step-role token, or null if unrecognized. */
export function roleForToken(token: string | undefined): Role | null {
  if (!token) return null
  return ROLE_BY_TOKEN[token] ?? null
}

export function tokenForRole(role: Role): UserRoleToken | undefined {
  return (USER_ROLE_TOKENS as Partial<Record<Role, UserRoleToken>>)[role]
}

/** The internal step roles a person can be granted directly. The three approval
    tiers and fleet maintenance are NOT here — those are appointments in
    `assignments`, and the appointment row is the permission. */
export const GRANTABLE_ROLES: readonly Role[] = [
  'Admin',
  'ImportControl',
  'ExportControl',
  'ClosingConfirmation',
  'ManualEntry',
  'DenialReview',
  'Training',
]

export interface StepGroupSpec {
  role: Role
  title: string
  blurb: string
}

// The Assignments tab is now three screens — Admin, Accounting Assignments,
// Approval Assignments — each reading top to bottom in a fixed order. Ordering
// lives here rather than inside the screen so it is real data a test can read,
// not a shape inferred from source text.
//
// ONE THING, FOUR NAMES: the UI role value `'Training'` is the stored token
// `references`, the group title `'References'`, and the screen file
// `TrainingScreen.tsx`. The team keeps the name `Training` (renaming a
// Dataverse team is a deploy) and the group and screen are titled for what the
// team actually does now. Do not invent a fifth name for it.

/** The Admin screen. Just the one group — appointing here is admin-only, so
    there is nothing to show a non-admin (`visibleAssignmentScreens`). */
export const ADMIN_STEP_GROUPS: readonly StepGroupSpec[] = [
  {
    role: 'Admin',
    title: 'Admin',
    blurb: 'Full control of this page: may appoint anyone to any group, including Admin.',
  },
]

/** The Accounting Assignments screen: every internal step role except Admin. */
export const ACCOUNTING_STEP_GROUPS: readonly StepGroupSpec[] = [
  {
    role: 'ImportControl',
    title: 'Import Control',
    blurb: 'Reviews each document at intake, fixes the extracted data, and releases it for approval.',
  },
  { role: 'ExportControl', title: 'Export Control', blurb: 'Final approval, then builds the Point Click Care file.' },
  { role: 'ClosingConfirmation', title: 'Closing Confirmation', blurb: 'Confirms the Point Click Care import here once it has been uploaded there.' },
  { role: 'ManualEntry', title: 'Manual Entry', blurb: 'Hand-keys anything into Point Click Care that does not fit the import path.' },
  { role: 'DenialReview', title: 'Denial Review', blurb: 'Finalizes anything denied at any approval step.' },
  { role: 'Training', title: 'References', blurb: 'Maintains the vendor, company, account, default and vehicle lists.' },
]
